import { type JSX } from 'react';

import { DocumentApi } from 'Api/Document/DocumentApi';
import { Button } from 'Components/Buttons/Buttons';
import { UploadedFileAndState } from 'Components/Files/UploadedFileAndState';
import { Modal } from 'Components/Modal/Modal';
import { ColumnConfig, ColumnType } from 'Components/Table/Columns';
import { Table } from 'Components/Table/Table';
import { ICON_CLOSE } from 'Config/Icons';
import { iso8601ToUsDateShort } from 'Helpers/DateTimeUtils/DateTimeUtils';
import { DueDiligenceWorkflowFileWithSource } from 'Models/DueDiligenceWorkflow';
import { TprmOrUcrmDueDiligenceWorkflowArchive } from 'Models/UcrmTprm';

import styles from './DueDiligenceWorkflowFilesModal.module.css';

export interface DueDiligenceWorkflowFilesModalProps {
    categorizedFiles: DueDiligenceWorkflowFileWithSource[];
    documentApi: DocumentApi;
    hideModal: () => void;
    workflowArchive: TprmOrUcrmDueDiligenceWorkflowArchive;
}

/**
 * Renders a modal containing a table of all files from a historical due diligence workflow (`workflowArchive`). Used on the TPRM Service Dashboard and the UCRM Use Case Dashboard. Files are categorized by source (inherent risk questionnaire, assessment, etc.).
 */
export const DueDiligenceWorkflowFilesModal = (props: DueDiligenceWorkflowFilesModalProps): JSX.Element => {
    const columns: ColumnConfig<DueDiligenceWorkflowFileWithSource>[] = [
        {
            type: ColumnType.CUSTOM,
            name: 'Name',
            getValue: (file) => file.filename,
            getTableCellContent: (file) => <UploadedFileAndState documentApi={props.documentApi} file={file} onDownloadError={console.log} />,
            sortComparator: (a: string, b: string) => a.localeCompare(b),
        },
        {
            type: ColumnType.STRING,
            name: 'Source',
            getString: (file) => file.source,
        },
        {
            type: ColumnType.DATE,
            name: 'Uploaded Date',
            getDateString: (file) => file.created_timestamp,
        },
    ];

    return (
        <Modal open header="Due Diligence Workflow Files" subHeader={`Workflow completed on ${iso8601ToUsDateShort(props.workflowArchive.timestamp)}`}>
            <div className={styles.tableContainer}>
                <Table<DueDiligenceWorkflowFileWithSource> columns={columns} defaultSortColumn="Name" filters={[]} getUniqueIdForRow={(file) => file.file_id} rowsData={props.categorizedFiles} />
            </div>
            <div className={'modalFormButtonContainer'}>
                <Button variant="secondary" onClick={props.hideModal} fontAwesomeImage={ICON_CLOSE}>
                    Close
                </Button>
            </div>
        </Modal>
    );
};
